import { useState, useEffect, useCallback } from 'react';
import { Button, Input, Card, useToast } from '../components/common';
import api from '../services/api';
import type { Settings, RetryStep, CallMethod } from '../types';

const defaultRetryStrategy: RetryStep[] = [
  { id: '1', method: 'cloudflare', max_failures: 2, delay_seconds: 1 },
  { id: '2', method: 'cloudflare_key', max_failures: 2, delay_seconds: 1 },
  { id: '3', method: 'vps', max_failures: 2, delay_seconds: 1 },
];

export default function SettingsPage() {
  const { addToast } = useToast();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get<Settings>('/api/v1/settings');
      const data = response.data;
      if (!data.retry_strategy || data.retry_strategy.length === 0) data.retry_strategy = defaultRetryStrategy;
      if (!data.vps_relay_url) data.vps_relay_url = '';
      if (!data.vps_api_secret) data.vps_api_secret = '';
      setSettings(data);
    } catch (err) {
      addToast('error', '載入失敗');
      setSettings({ id: 0, log_retention_days: 30, retry_strategy: defaultRetryStrategy, vps_relay_url: '', vps_api_secret: '', updated_at: '' });
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      const normalizedStrategy = settings.retry_strategy.map(step => ({
        id: step.id, method: step.method,
        max_failures: Math.max(1, Math.min(10, Math.floor(Number(step.max_failures) || 1))),
        delay_seconds: Math.max(0, Math.min(60, Math.floor(Number(step.delay_seconds) || 0))),
      }));
      const payload = { retry_strategy: normalizedStrategy, log_retention_days: Math.max(1, Math.min(365, Math.floor(Number(settings.log_retention_days) || 30))), vps_relay_url: settings.vps_relay_url || '', vps_api_secret: settings.vps_api_secret || '' };
      const response = await api.put<Settings>('/api/v1/settings', payload);
      if (response.data) setSettings(response.data);
      addToast('success', '已儲存');
    } catch (err: any) { addToast('error', err.response?.data?.error?.message || '儲存失敗'); } finally { setSaving(false); }
  };

  const handleDragStart = (index: number) => setDraggedIndex(index);
  const handleDragEnd = () => setDraggedIndex(null);
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index || !settings) return;
    const newStrategy = [...settings.retry_strategy];
    const [draggedItem] = newStrategy.splice(draggedIndex, 1);
    newStrategy.splice(index, 0, draggedItem);
    setSettings({ ...settings, retry_strategy: newStrategy });
    setDraggedIndex(index);
  };

  const updateRetryStep = useCallback((idx: number, field: keyof RetryStep, val: number | string) => {
    if (!settings) return;
    const newStrategy = [...settings.retry_strategy];
    newStrategy[idx] = { ...newStrategy[idx], [field]: val };
    setSettings({ ...settings, retry_strategy: newStrategy });
  }, [settings]);

  const addStep = () => {
    if (!settings) return;
    setSettings({ ...settings, retry_strategy: [...settings.retry_strategy, { id: String(Date.now()), method: 'cloudflare', max_failures: 2, delay_seconds: 1 }] });
  };

  const removeStep = (idx: number) => {
    if (!settings || settings.retry_strategy.length <= 1) return;
    setSettings({ ...settings, retry_strategy: settings.retry_strategy.filter((_, i) => i !== idx) });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>;
  if (!settings) return <div className="text-center py-8 text-gray-500">無法載入設置</div>;

  const getRowClass = (index: number) => draggedIndex === index ? "flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 cursor-move border-primary-500 opacity-50" : "flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 cursor-move border-transparent";
  const hasVpsStep = settings.retry_strategy.some(s => s.method === 'vps');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">系統設置</h1>
        <Button onClick={handleSave} disabled={saving}>{saving ? '儲存中...' : '儲存'}</Button>
      </div>
      <Card>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">重試策略</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">拖曳調整順序，設定每個步驟的調用方式、失敗次數和延遲時間</p>
        </div>
        <div className="p-4 space-y-3">
          {settings.retry_strategy.map((step, index) => (
            <div key={step.id} draggable onDragStart={() => handleDragStart(index)} onDragOver={(e) => handleDragOver(e, index)} onDragEnd={handleDragEnd} className={getRowClass(index)}>
              <span className="font-medium text-gray-700 dark:text-gray-300 w-6">{index + 1}</span>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                <select value={step.method} onChange={(e) => updateRetryStep(index, 'method', e.target.value as CallMethod)} className="px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="cloudflare">Cloudflare 直連</option>
                  <option value="cloudflare_key">Cloudflare + KEY</option>
                  <option value="vps">VPS 轉發</option>
                </select>
                <div className="flex items-center gap-2"><label className="text-sm text-gray-600 dark:text-gray-400">失敗次數:</label><input type="number" min="1" max="10" value={step.max_failures} onChange={(e) => updateRetryStep(index, 'max_failures', parseInt(e.target.value) || 1)} className="w-16 px-2 py-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" /></div>
                <div className="flex items-center gap-2"><label className="text-sm text-gray-600 dark:text-gray-400">延遲(秒):</label><input type="number" min="0" max="60" value={step.delay_seconds} onChange={(e) => updateRetryStep(index, 'delay_seconds', parseInt(e.target.value) || 0)} className="w-16 px-2 py-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" /></div>
                <button onClick={() => removeStep(index)} disabled={settings.retry_strategy.length <= 1} className="text-red-500 hover:text-red-700 disabled:opacity-30">刪除</button>
              </div>
            </div>
          ))}
          <Button variant="secondary" onClick={addStep}>+ 新增步驟</Button>
        </div>
      </Card>
      <Card>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700"><h2 className="text-lg font-semibold text-gray-900 dark:text-white">日誌設置</h2></div>
        <div className="p-4"><Input label="日誌保留天數" type="number" min="1" max="365" value={settings.log_retention_days.toString()} onChange={(e) => setSettings({ ...settings, log_retention_days: parseInt(e.target.value) || 30 })} /></div>
      </Card>
      {hasVpsStep && (<Card>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700"><h2 className="text-lg font-semibold text-gray-900 dark:text-white">VPS 中繼服務</h2><p className="text-sm text-gray-500 dark:text-gray-400 mt-1">當重試策略包含「VPS 轉發」時需要配置</p></div>
        <div className="p-4 space-y-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-800 dark:text-blue-200"><p className="font-medium mb-1">說明：</p><p>VPS 轉發會將請求發送到你的 VPS，VPS 會先用自己的 IP 嘗試，失敗後自動使用動態代理。</p></div>
          <Input label="VPS 中繼 URL" value={settings.vps_relay_url} onChange={(e) => setSettings({ ...settings, vps_relay_url: e.target.value })} placeholder="https://your-vps.com/relay" />
          <Input label="API 密鑰" type="password" value={settings.vps_api_secret} onChange={(e) => setSettings({ ...settings, vps_api_secret: e.target.value })} placeholder="VPS 中繼服務的 API 密鑰" />
        </div>
      </Card>)}
    </div>
  );
}
